import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from '@auth/services/auth.service';
import { validateApiResponseWithData, validateApiResponseArray } from '@shared/validators/runtime.validators';
import { Quote } from '../interfaces/quote.interface';
import { QuoteListItem } from '../interfaces/quote-list-item.interface';
import { QuoteCoverageBreakdown } from '../interfaces/quote-coverage.interface';
import {
  CreateQuoteRequest,
  CreateQuoteCoverageRequest,
  UpdateQuoteRequest,
  QuoteListFilters,
  CalculateQuoteResponse,
  ApiResponse,
  PagedResponse,
  QuoteIntelligentListItem,
  QuoteIntelligentListFilters,
  SaveHashRequest,
  SaveHashResponse,
  EmprendeSeguroQuoteListItem,
  SaveHashEmprendeSeguroResponse,
  HogarSeguroQuoteListItem,
  ArmaloTuDiscount,
  AddArmaloTuDiscountRequest
} from '../interfaces/quote-request.interface';

const baseUrl = environment.baseUrl;

@Injectable({ providedIn: 'root' })
export class QuoteService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  // Totales de la cotización actual
  readonly premium = signal<number>(0);
  readonly discount = signal<number>(0);
  readonly tax = signal<number>(0);
  readonly total = signal<number>(0);
  readonly deductible = signal<number>(0);

  /**
   * Actualiza los totales almacenados
   */
  updateTotals(totals: { premium: number; discount: number; tax: number; total: number; deductible?: number }): void {
    this.premium.set(totals.premium || 0);
    this.discount.set(totals.discount || 0);
    this.tax.set(totals.tax || 0);
    this.total.set(totals.total || 0);
    if (totals.deductible !== undefined) {
      this.deductible.set(totals.deductible || 0);
    }
  }

  /**
   * Restablece los totales a cero
   */
  resetTotals(): void {
    this.premium.set(0);
    this.discount.set(0);
    this.tax.set(0);
    this.total.set(0);
    this.deductible.set(0);
  }

  /**
   * Obtiene la lista de cotizaciones con filtros opcionales
   */
  getQuotes(filters?: QuoteListFilters): Observable<PagedResponse<QuoteListItem>> {
    let params = new HttpParams();

    if (filters?.quoteTypeCode) {
      params = params.set('quoteTypeCode', filters.quoteTypeCode.toString());
    }
    if (filters?.producerNumber) {
      params = params.set('producerNumber', filters.producerNumber.toString());
    }
    if (filters?.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters?.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters?.pageNumber) {
      params = params.set('pageNumber', filters.pageNumber.toString());
    }
    if (filters?.pageSize) {
      params = params.set('pageSize', filters.pageSize.toString());
    }

    const url = `${baseUrl}/v${environment.apiVersion}/Quotes`;

    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.get<PagedResponse<QuoteListItem>>(
      url,
      { params }
    );
  }

  getQuoteById(id: number): Observable<ApiResponse<Quote>> {
    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.get<ApiResponse<Quote>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}`
    ).pipe(
      tap(response => {
        if (response.succeeded && response.data) {
          this.updateTotals({
            premium: response.data.netPremium,
            discount: response.data.discount,
            tax: response.data.tax,
            total: response.data.totalPremium,
            deductible: response.data.vehicleDeductible || 0
          });
        }
      })
    );
  }

  /**
   * Crea una nueva cotización
   *
   * NOTA: Según el nuevo flujo del API:
   * - Este endpoint crea la cotización SIN coberturas
   * - Las coberturas se crean automáticamente durante POST /Quotes/{id}/calculate
   * - Por lo tanto, coverages en el request es opcional y será ignorado
   */
  createQuote(quote: CreateQuoteRequest): Observable<ApiResponse<number>> {
    // Si producerCode es 0 o no se especifica, usar el del usuario autenticado
    const producerNum = quote.producerCode || this.authService.user()?.producerNumber || 0;
    const request = {
      ...quote,
      producerCode: producerNum,
      producerNumber: producerNum
    };

    // Debug: Log del request completo que se envía al API
    console.log('[QuoteService] createQuote - Request completo que se envía al API:', {
      url: `${baseUrl}/v${environment.apiVersion}/Quotes`,
      method: 'POST',
      body: JSON.stringify(request, null, 2),
      personalityCode: request.personalityCode,
      personalityCodeType: typeof request.personalityCode,
      clientId: request.clientId,
      clientFirstName: request.clientFirstName,
      clientLastName: request.clientLastName,
      quoteTypeCode: request.quoteTypeCode,
      vehicle: request.vehicle ? {
        vehicleValue: request.vehicle.vehicleValue,
        vehicleYear: request.vehicle.vehicleYear,
        idQuoteIntelligentPlan: request.vehicle.idQuoteIntelligentPlan
      } : undefined,
      vehicles: request.vehicles ? {
        count: request.vehicles.length,
        vehicles: request.vehicles.map(v => ({
          vehicleValue: v.vehicleValue,
          vehicleYear: v.vehicleYear,
          idQuoteIntelligentPlan: v.idQuoteIntelligentPlan
        }))
      } : undefined
    });

    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.post<ApiResponse<number>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes`,
      request
    );
  }

  /**
   * Actualiza una cotización existente
   */
  updateQuote(id: number, quote: UpdateQuoteRequest): Observable<ApiResponse<number>> {
    // Si producerCode es 0 o no se especifica, usar el del usuario autenticado
    // NOTA: Se usa el username (email) porque el backend legacy suele esperar esto como Cuser
    const user = this.authService.user()?.username || '';
    const producerNum = quote.producerCode || this.authService.user()?.producerNumber || 0;
    const request = {
      ...quote,
      coverages: null, // Evitar que el PUT intente insertar coberturas sin Cuser. Se manejarán en calculateQuote.
      producerCode: producerNum,
      producerNumber: producerNum,
      cuser: user,
      Cuser: user,
      userName: user,
      userID: user,
      emailAddress: user,
      User: user,
      quoteNumber: id
    };

    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.put<ApiResponse<number>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}`,
      request
    ).pipe(
      map(response => {
        validateApiResponseWithData(response, `updateQuote(${id})`);
        return response;
      })
    );
  }

  /**
   * Elimina una cotización
   */
  deleteQuote(id: number): Observable<ApiResponse<number>> {
    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.delete<ApiResponse<number>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}`
    ).pipe(
      map(response => {
        validateApiResponseWithData(response, `deleteQuote(${id})`);
        return response;
      })
    );
  }

  /**
   * Elimina un vehículo de una cotización flotilla eliminando y recreando la cotización
   * con los vehículos restantes. Esto mantiene sincronizado el backend con el estado local.
   * @param quoteId - ID de la cotización a eliminar
   */
  deleteFleetQuote(quoteId: number): Observable<ApiResponse<number>> {
    return this.http.delete<ApiResponse<number>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${quoteId}`
    ).pipe(
      map(response => {
        validateApiResponseWithData(response, `deleteFleetQuote(${quoteId})`);
        return response;
      })
    );
  }

  /**
   * Calcula la prima de una cotización
   * @param id - ID de la cotización
   * @param coverages - Array opcional de coberturas seleccionadas. Si se proporciona, solo estas coberturas se incluirán en el cálculo.
   * @param expandCoverage - Valor opcional de cobertura expandida (ExpandCoverage). Si se proporciona, se usará este valor en lugar del valor por defecto.
   */
  calculateQuote(id: number, coverages?: CreateQuoteCoverageRequest[], expandCoverage?: number, producerCode?: number, includeTax?: boolean, vehicleExpandCoverages?: { vehicleNumber: number; expandCoverage: number }[], skipVehicleNumbers?: number[], vehicleCoverageTypes?: { vehicleNumber: number; quoteTypeCode: number }[]): Observable<ApiResponse<CalculateQuoteResponse>> {
    const body: { coverages?: CreateQuoteCoverageRequest[]; expandCoverage?: number; vehicleExpandCoverages?: { vehicleNumber: number; expandCoverage: number }[]; producerCode?: number | string; producerNumber?: number; includeTax?: boolean; skipVehicleNumbers?: number[]; vehicleCoverageTypes?: { vehicleNumber: number; quoteTypeCode: number }[] } = {};

    const user = this.authService.user()?.username || '';
    const producerNum = producerCode || this.authService.user()?.producerNumber || 0;

    if (coverages && coverages.length > 0) {
      // Inyectar cuser en cada cobertura por si el backend mapea individualmente
      body.coverages = coverages.map(c => ({
        ...c,
        cuser: user,
        Cuser: user,
        User: user,
        userName: user,
        userID: user
      }));
    }

    if (expandCoverage !== undefined && expandCoverage !== null) {
      body.expandCoverage = expandCoverage;
    }

    // Ampliar Cobertura POR VEHÍCULO (flotilla): cada vehículo conserva su propio expand
    // para no heredar el del vehículo en edición al recalcular.
    if (vehicleExpandCoverages && vehicleExpandCoverages.length > 0) {
      body.vehicleExpandCoverages = vehicleExpandCoverages;
    }

    if (includeTax !== undefined && includeTax !== null) {
      body.includeTax = includeTax;
    }

    // Tipo de cobertura REAL por unidad (Full / Ley). El encabezado de una flotilla dice
    // Flotilla (3) y no lo distingue, así que se declara aquí para que el motor no lo deduzca.
    if (vehicleCoverageTypes && vehicleCoverageTypes.length > 0) {
      body.vehicleCoverageTypes = vehicleCoverageTypes;
    }

    // PARIDAD LEGACY (flotilla): los vehículos ya calculados NO se recalculan; conservan su prima.
    if (skipVehicleNumbers && skipVehicleNumbers.length > 0) {
      body.skipVehicleNumbers = skipVehicleNumbers;
    }

    // Incluir múltiples variaciones de identificación de usuario
    body.producerCode = producerNum;
    body.producerNumber = producerNum;
    (body as any).cuser = user;
    (body as any).Cuser = user;
    (body as any).userName = user;
    (body as any).userID = user;
    (body as any).emailAddress = user;
    (body as any).User = user;

    // Debug: Log del body completo que se envía al API
    console.log('[QuoteService] calculateQuote - Request completo que se envía al API:', {
      url: `${baseUrl}/v${environment.apiVersion}/Quotes/${id}/calculate`,
      method: 'POST',
      body: JSON.stringify(body, null, 2),
      quoteId: id,
      coveragesCount: coverages?.length || 0,
      coverages: coverages?.map(c => ({
        code: c.coverageCode,
        limit: c.coverageLimit,
        selected: c.selectedCoverage,
        coinsurance: c.coinsurancePercent,
        expand: c.expandPercent,
        discount: c.userDiscount
      })) || [],
      expandCoverage: body.expandCoverage,
      expandCoverageType: typeof body.expandCoverage
    });

    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.post<ApiResponse<CalculateQuoteResponse>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}/calculate`,
      body
    ).pipe(
      tap(response => {
        if (response.succeeded && response.data) {
          this.updateTotals({
            premium: response.data.netPremium,
            discount: response.data.discount || response.data.totalDiscount,
            tax: response.data.tax,
            total: response.data.totalPremium,
            deductible: response.data.vehicleDeductible || 0
          });
        }
      })
    );
  }

  /**
   * Elimina UNA unidad de una flotilla, sin recrear la cotización ni recalcular las demás.
   * PARIDAD LEGACY: equivale al borrado de fila del grid de flotilla del legacy.
   */
  deleteQuoteVehicle(id: number, vehicleNumber: number): Observable<ApiResponse<number>> {
    return this.http.delete<ApiResponse<number>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}/vehicles/${vehicleNumber}`
    );
  }

  /**
   * Obtiene el desglose de coberturas de una cotización
   */
  getCoverageBreakdown(id: number, vehicleNumber: number = 1): Observable<ApiResponse<QuoteCoverageBreakdown[]>> {
    let params = new HttpParams();
    params = params.set('vehicleNumber', vehicleNumber.toString());

    // El interceptor HTTP agrega automáticamente el token de autenticación
    return this.http.get<ApiResponse<QuoteCoverageBreakdown[]>>(
      `${baseUrl}/v${environment.apiVersion}/Quotes/${id}/coverage-breakdown`,
      { params }
    ).pipe(
      map(response => {
        validateApiResponseArray(response, `getCoverageBreakdown(${id})`);
        return response;
      })
    );
  }

  /**
   * Obtiene el reporte PDF de una cotización de vehículo.
   * GET /api/v1/Report/quote-vehicle/pdf
   * Parámetros: quoteNumber (obligatorio), planId (opcional, default 0), quoteType ("Full" | "Ley", default "Full").
   */
  getQuoteVehicleReportPdf(quoteNumber: number, planId: number = 0, quoteType: string = 'Full'): Observable<Blob> {
    const reportUrl = `${baseUrl}/v${environment.apiVersion}/Report/quote-vehicle/pdf`;
    const params = new HttpParams()
      .set('quoteNumber', quoteNumber.toString())
      .set('planId', planId.toString())
      .set('quoteType', quoteType);
    return this.http.get(reportUrl, { params, responseType: 'blob' });
  }

  /**
   * Obtiene la lista inteligente de cotizaciones (Armalo Tu)
   */
  getIntelligentQuotes(filters: QuoteIntelligentListFilters): Observable<PagedResponse<QuoteIntelligentListItem>> {
    let params = new HttpParams();

    if (filters.quoteTypeCode) {
      params = params.set('quoteTypeCode', filters.quoteTypeCode.toString());
    }
    if (filters.producerNumber) {
      params = params.set('producerNumber', filters.producerNumber.toString());
    }
    if (filters.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters.programmerLog) {
      params = params.set('programmerLog', filters.programmerLog);
    }
    if (filters.pageNumber) {
      params = params.set('pageNumber', filters.pageNumber.toString());
    }
    if (filters.pageSize) {
      params = params.set('pageSize', filters.pageSize.toString());
    }

    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/intelligent-list`;
    return this.http.get<PagedResponse<QuoteIntelligentListItem>>(url, { params });
  }

  /**
   * Obtiene el reporte PDF de una cotización de Ármalo Tú
   * @param quoteNumber Número de cotización
   * @param email Correo electrónico del cliente
   */
  getReportQuoteArmaloTu(quoteNumber: number, email: string): Observable<Blob> {
    const url = `${baseUrl}/v${environment.apiVersion}/Report/quote-armlo-tu/pdf`;
    const params = new HttpParams()
      .set('QuoteNumber', quoteNumber.toString())
      .set('Email', email);
    
    return this.http.get(url, { params, responseType: 'blob' });
  }

  /**
   * Obtiene el reporte PDF de una cotización de Emprende Seguro
   * @param quoteNumber Número de cotización
   * @param email Correo electrónico del prospecto
   */
  getReportQuoteEmprendeSeguro(quoteNumber: number, email: string): Observable<Blob> {
    const url = `${baseUrl}/v${environment.apiVersion}/Report/emprende-seguro/pdf`;
    const params = new HttpParams()
      .set('QuoteId', quoteNumber.toString())
      .set('Email', email);
    
    return this.http.get(url, { params, responseType: 'blob' });
  }

  /**
   * Obtiene el reporte PDF de una cotización de Hogar Seguro
   * @param quoteNumber Número de cotización
   * @param email Correo electrónico del usuario
   */
  getReportQuoteHogarSeguro(quoteNumber: number, email: string): Observable<Blob> {
    const url = `${baseUrl}/v${environment.apiVersion}/Report/hogar-seguro/pdf`;
    const params = new HttpParams()
      .set('QuoteId', quoteNumber.toString())
      .set('Email', email);

    return this.http.get(url, { params, responseType: 'blob' });
  }

  /**
   * Guarda un hash para redirección a Armalo Tu
   */
  saveHash(request: SaveHashRequest): Observable<SaveHashResponse> {
    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/save-hash`;
    return this.http.post<SaveHashResponse>(url, request);
  }

  /**
   * Obtiene la lista de cotizaciones de Emprende Seguro
   */
  getEmprendeSeguroQuotes(filters: QuoteIntelligentListFilters): Observable<PagedResponse<EmprendeSeguroQuoteListItem>> {
    let params = new HttpParams();

    if (filters.producerNumber) {
      params = params.set('producerNumber', filters.producerNumber.toString());
    }
    if (filters.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters.programmerLog) {
      params = params.set('programmerLog', filters.programmerLog);
    }
    if (filters.pageNumber) {
      params = params.set('pageNumber', filters.pageNumber.toString());
    }
    if (filters.pageSize) {
      params = params.set('pageSize', filters.pageSize.toString());
    }

    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/emprende-seguro-list`;

    return this.http.get<PagedResponse<EmprendeSeguroQuoteListItem>>(url, { params });
  }

  /**
   * Guarda un hash para redirección a Emprende Seguro
   */
  saveHashEmprendeSeguro(request: SaveHashRequest): Observable<SaveHashEmprendeSeguroResponse> {
    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/save-hash-quote`;
    return this.http.post<SaveHashEmprendeSeguroResponse>(url, request);
  }

  /**
   * Obtiene la lista de cotizaciones de Hogar Seguro
   */
  getHogarSeguroQuotes(filters: QuoteIntelligentListFilters): Observable<PagedResponse<HogarSeguroQuoteListItem>> {
    let params = new HttpParams();

    if (filters.producerNumber) {
      params = params.set('producerNumber', filters.producerNumber.toString());
    }
    if (filters.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters.programmerLog) {
      params = params.set('programmerLog', filters.programmerLog);
    }
    if (filters.pageNumber) {
      params = params.set('pageNumber', filters.pageNumber.toString());
    }
    if (filters.pageSize) {
      params = params.set('pageSize', filters.pageSize.toString());
    }

    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/hogar-seguro-list`;

    return this.http.get<PagedResponse<HogarSeguroQuoteListItem>>(url, { params });
  }

  /**
   * Obtiene los últimos 30 descuentos de Ármalo Tú aplicados por el usuario
   */
  getArmaloTuDiscounts(): Observable<ApiResponse<ArmaloTuDiscount[]>> {
    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/armalo-tu-discounts`;
    return this.http.get<ApiResponse<ArmaloTuDiscount[]>>(url);
  }

  /**
   * Agrega un nuevo descuento de Ármalo Tú
   */
  addArmaloTuDiscount(request: AddArmaloTuDiscountRequest): Observable<ApiResponse<boolean>> {
    const url = `${baseUrl}/v${environment.apiVersion}/Quotes/armalo-tu-discounts`;
    return this.http.post<ApiResponse<boolean>>(url, request);
  }
}

